<script setup>
import { computed, useSlots, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import SzCallButton from 'dashboard/components/widgets/conversation/solarz/SzCallButton.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';


const props = defineProps({
  selectedContact: {
    type: Object,
    default: () => ({}),
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['goToContactsList', 'toggleBlock', 'deleteContact']);

const { t } = useI18n();
const slots = useSlots();
const route = useRoute();
const store = useStore();

const isContactSidebarOpen = ref(false);
// SZ: estado do dropdown "..." do header (Bloquear / Iniciar ligação)
const moreOpen = ref(false);
const closeMore = () => { moreOpen.value = false; };

// SZ_CALL_DROPDOWN_FIX_20260525: NÃO fecha o dropdown ao clicar "Iniciar ligação".
// Fechar moreOpen=false sincrono desmontava o <SzCallButton> (que está dentro do
// `<div v-if="moreOpen">`) ANTES do Vue conseguir renderizar o <Teleport>
// do dialog de confirmação — dialog ficava com showConfirmDialog=true mas nunca
// chegava no DOM. Mantém dropdown aberto; v-on-click-outside fecha quando o
// usuário clica no dialog (= fora do dropdown).
const onCallTrigger = (open) => {
  if (typeof open === 'function') open();
};

const contactId = computed(() => route.params.contactId);

const selectedContactName = computed(() => {
  return props.selectedContact?.name;
});

// SZ: data de criação formatada DD/MM/YYYY pra subtitle do header
// (substitui o identifier/ref do canal anterior).
const selectedContactCreatedAt = computed(() => {
  const raw =
    props.selectedContact?.created_at ||
    props.selectedContact?.createdAt;
  if (!raw) return '';
  const d = new Date(typeof raw === 'number' ? raw * 1000 : raw);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS'),
      link: '#',
    },
  ];
  if (props.selectedContact) {
    items.push({
      label: selectedContactName.value,
    });
  }
  return items;
});

const isContactBlocked = computed(() => {
  return props.selectedContact?.blocked;
});

const handleBreadcrumbClick = () => {
  emit('goToContactsList');
};

const toggleBlock = () => {
  emit('toggleBlock', isContactBlocked.value);
};

const handleConversationSidebarToggle = () => {
  isContactSidebarOpen.value = !isContactSidebarOpen.value;
};

const closeMobileSidebar = () => {
  if (!isContactSidebarOpen.value) return;
  isContactSidebarOpen.value = false;
};
</script>

<template>
  <section
    class="flex w-full h-full overflow-hidden bg-n-background"
  >
    <div
      class="flex flex-col w-full h-full transition-all duration-300"
    >
      <!-- SZ Figma: header 56px com botão voltar circular + título 2-linhas +
           "Enviar mensagem" outline + ⋮ (menu com Bloquear/Iniciar ligação). -->
      <header class="sticky top-0 z-10 sz-cd-header" v-on-click-outside="closeMore">
        <!-- Botão voltar (40x40 circular bg #E9EAED) -->
        <button
          type="button"
          class="sz-cd-back-btn"
          @click="handleBreadcrumbClick"
          :aria-label="t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS')"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        </button>

        <!-- Título + subtitle (data de criação) -->
        <div class="sz-cd-title-wrap">
          <h1 class="sz-cd-title">{{ selectedContactName || '...' }}</h1>
          <div class="sz-cd-subline">
            <span v-if="selectedContactCreatedAt" class="sz-cd-sub-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Criado em {{ selectedContactCreatedAt }}
            </span>
          </div>
        </div>

        <!-- Ações à direita: Enviar mensagem (153x40) + ⋮ (40x40) -->
        <div class="sz-cd-actions">
          <ComposeConversation :contact-id="contactId">
            <template #trigger="{ toggle }">
              <button type="button" class="sz-cd-send-btn" @click="toggle">
                {{ $t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE') }}
              </button>
            </template>
          </ComposeConversation>
          <div class="sz-cd-more-wrap">
            <button
              type="button"
              class="sz-cd-more-btn"
              :class="{ 'sz-cd-more-btn--active': moreOpen }"
              aria-label="Mais ações"
              @click.stop="moreOpen = !moreOpen"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <circle cx="5" cy="12" r="1.8"/>
                <circle cx="12" cy="12" r="1.8"/>
                <circle cx="19" cy="12" r="1.8"/>
              </svg>
            </button>
            <div v-if="moreOpen" class="sz-cd-more-menu">
              <SzCallButton
                :contact-id="selectedContact?.id"
                :contact="selectedContact"
                variant="button"
              >
                <template #trigger="{ open }">
                  <button
                    type="button"
                    class="sz-cd-more-item"
                    @click="onCallTrigger(open)"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    Iniciar ligação
                  </button>
                </template>
              </SzCallButton>
              <button
                type="button"
                class="sz-cd-more-item"
                :disabled="isUpdating"
                @click="moreOpen = false; toggleBlock();"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                {{ !isContactBlocked ? $t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT') : $t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT') }}
              </button>
              <div class="sz-cd-more-sep" />
              <!-- SZ: Excluir contato — antes ficava no body do form, agora vive
                   só no menu do header. O evento sobe pra ContactManageView que
                   chama o método exposto pelo ContactDetails pra abrir o dialog. -->
              <button
                type="button"
                class="sz-cd-more-item sz-cd-more-item--danger"
                @click="moreOpen = false; emit('deleteContact');"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                {{ $t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
              </button>
            </div>
          </div>
        </div>
      </header>
      <main class="sz-cd-main flex-1 overflow-y-auto">
        <div class="w-full py-4">
          <slot name="default" />
        </div>
      </main>
    </div>

    <!-- Desktop sidebar -->
    <div
      v-if="slots.sidebar"
      class="sz-contact-sidebar bg-n-background hidden lg:flex flex-col h-full overflow-hidden md:w-[320px] md:min-w-[320px] 2xl:min-w-[360px] 2xl:w-[360px] ltr:border-l rtl:border-r border-n-weak"
    >
      <div class="flex-1 overflow-y-auto"><slot name="sidebar" /></div>
    </div>

    <!-- Mobile sidebar container -->
    <div
      v-if="slots.sidebar"
      class="lg:hidden fixed top-0 ltr:right-0 rtl:left-0 h-full z-50 flex justify-end transition-all duration-200 ease-in-out"
      :class="isContactSidebarOpen ? 'w-full' : 'w-16'"
    >
      <!-- Toggle button -->
      <div
        v-on-click-outside="[
          closeMobileSidebar,
          { ignore: ['#contact-sidebar-content'] },
        ]"
        class="flex items-start p-1 w-fit h-fit relative order-1 xs:top-24 top-28 transition-all bg-n-solid-2 border border-n-weak duration-500 ease-in-out"
        :class="[
          isContactSidebarOpen
            ? 'justify-end ltr:rounded-l-full rtl:rounded-r-full ltr:rounded-r-none rtl:rounded-l-none'
            : 'justify-center rounded-full ltr:mr-6 rtl:ml-6',
        ]"
      >
        <Button
          ghost
          slate
          sm
          class="!rounded-full rtl:rotate-180"
          :class="{ 'bg-n-alpha-2': isContactSidebarOpen }"
          :icon="
            isContactSidebarOpen
              ? 'i-lucide-panel-right-close'
              : 'i-lucide-panel-right-open'
          "
          data-contact-sidebar-toggle
          @click="handleConversationSidebarToggle"
        />
      </div>

      <Transition
        enter-active-class="transition-transform duration-200 ease-in-out"
        leave-active-class="transition-transform duration-200 ease-in-out"
        enter-from-class="ltr:translate-x-full rtl:-translate-x-full"
        enter-to-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-from-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-to-class="ltr:translate-x-full rtl:-translate-x-full"
      >
        <div
          v-if="isContactSidebarOpen"
          id="contact-sidebar-content"
          class="order-2 w-[85%] sm:w-[50%] bg-n-solid-2 ltr:border-l rtl:border-r border-n-weak overflow-y-auto py-6 shadow-lg"
        >
          <slot name="sidebar" />
        </div>
      </Transition>
    </div>
  </section>
</template>

<style scoped>
/* SZ Figma — background do <main> do detalhe = #FAFAFA (Figma #1652704732),
   cobre toda a área abaixo do header (não só o card interno). */
.sz-cd-main {
  background: #FAFAFA;
  padding: 0;
}
.dark .sz-cd-main { background: #17181B; }

/* SZ Figma #1652706387 — header do detalhe de contato (56px). */
.sz-cd-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px;
  gap: 8px;
  height: 56px;
  background: #FFFFFF;
  border-bottom: 1px solid #E5E5E5;
  box-sizing: border-box;
  font-family: 'Inter', system-ui, sans-serif;
}
.dark .sz-cd-header {
  background: #1D1E21;
  border-bottom-color: #2A2C30;
}

/* Botão voltar — circular 40x40 (Figma .button-navigation-master) */
.sz-cd-back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #E9EAED;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  padding: 8px;
  flex-shrink: 0;
  color: #000000;
  transition: background 0.12s;
}
.sz-cd-back-btn:hover { background: #DCDFE3; }
.dark .sz-cd-back-btn { background: #2A2C30; color: #FFFFFF; }
.dark .sz-cd-back-btn:hover { background: #34373B; }

/* Wrap título + subtitle */
.sz-cd-title-wrap {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex: 1;
  min-width: 0;
}
.sz-cd-title {
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #000000;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark .sz-cd-title { color: #FFFFFF; }
.sz-cd-subline {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  height: 20px;
}
.sz-cd-sub-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: #525252;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sz-cd-sub-item svg { flex-shrink: 0; color: #525252; }
.dark .sz-cd-sub-item { color: #9797A0; }
.dark .sz-cd-sub-item svg { color: #9797A0; }

/* Ações à direita */
.sz-cd-actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  height: 40px;
}

/* Botão "Enviar mensagem" (Figma 153x40, border 1.5px #D4D4D4) */
.sz-cd-send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 16px;
  border: 1.5px solid #D4D4D4;
  border-radius: 8px;
  background: #FFFFFF;
  font-family: inherit;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s;
}
.sz-cd-send-btn:hover {
  background: #F9FAFB;
  border-color: #BDBDBD;
}
.dark .sz-cd-send-btn {
  background: #1D1E21;
  border-color: #3D3F43;
  color: #FFFFFF;
}
.dark .sz-cd-send-btn:hover { background: #26282B; }

/* Botão "..." (Figma 40x40, border 1.5px #D4D4D4) */
.sz-cd-more-wrap { position: relative; }
.sz-cd-more-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 8px;
  border: 1.5px solid #D4D4D4;
  border-radius: 8px;
  background: #FFFFFF;
  cursor: pointer;
  color: #000000;
  transition: background 0.12s, border-color 0.12s;
}
.sz-cd-more-btn:hover { background: #F9FAFB; }
.sz-cd-more-btn--active { background: #F0F0F0; border-color: #BDBDBD; }
.dark .sz-cd-more-btn {
  background: #1D1E21;
  border-color: #3D3F43;
  color: #FFFFFF;
}
.dark .sz-cd-more-btn:hover { background: #26282B; }
.dark .sz-cd-more-btn--active { background: #2E3033; border-color: #4A4D52; }

/* Menu do "..." */
.sz-cd-more-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 50;
  min-width: 200px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.12);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sz-cd-more-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1F2937;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  font-family: inherit;
  transition: background 0.1s ease;
}
.sz-cd-more-item:hover:not(:disabled) { background: #F3F4F6; }
.sz-cd-more-item:disabled { opacity: 0.55; cursor: not-allowed; }
.sz-cd-more-item svg { color: #525252; flex-shrink: 0; }
.sz-cd-more-item--danger { color: #B91C1C; }
.sz-cd-more-item--danger:hover:not(:disabled) { background: #FEE2E2; }
.sz-cd-more-item--danger svg { color: #B91C1C; }
.sz-cd-more-sep { height: 1px; background: #E5E7EB; margin: 4px 6px; }
.dark .sz-cd-more-sep { background: #3D3F43; }
.dark .sz-cd-more-item--danger { color: #FCA5A5; }
.dark .sz-cd-more-item--danger:hover:not(:disabled) { background: rgba(239, 68, 68, 0.15); }
.dark .sz-cd-more-item--danger svg { color: #FCA5A5; }
.dark .sz-cd-more-menu {
  background: #1D1E21;
  border-color: #3D3F43;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
}
.dark .sz-cd-more-item { color: #E5E7EB; }
.dark .sz-cd-more-item:hover:not(:disabled) { background: #242528; }
.dark .sz-cd-more-item svg { color: #9797A0; }
</style>
